/**
 * CollapsibleText — collapsed-by-default excerpt for ingested message
 * content (ticket comments, Slack messages, risk narratives, AI summaries)
 * that expands IN PLACE. No modal, no navigation.
 *
 * Rules this component enforces (see the 2026-08-15 "Manual Testing" fix):
 *
 * - The expand affordance renders only when the content actually overflows
 *   its clamp — measured (scrollHeight vs clientHeight, re-checked on
 *   resize and after web-font swap), never guessed from a character count,
 *   because these are variable-width fonts. A control that expands two
 *   words of nothing is noise; a clipped row with no affordance is a bug.
 * - The toggle is a real <button> with aria-expanded/aria-controls and the
 *   app's standard focus treatment — not a div with an onClick (the exact
 *   defect v2026.8.19 removed from ticket rows).
 * - Expansion state is per-instance. Callers rendering this in a list, or
 *   in a slot whose backing item can change on refetch, MUST key it by a
 *   stable identity (ticketId, facet name…) so a data refresh cannot
 *   reattach an open state to a different row. Do not key on array index
 *   or on LLM-generated ids.
 * - Expanded content is height-capped with internal scroll so one enormous
 *   comment thread cannot blow the host panel's layout apart.
 *
 * Clicks on the toggle do not bubble: several hosts (TicketCard, FeedRow)
 * are clickable containers and expanding must not also navigate.
 */
import { useId, useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

// Written out literally so Tailwind's scanner sees every class.
const CLAMP: Record<number, string> = {
  1: 'line-clamp-1',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
  5: 'line-clamp-5',
  6: 'line-clamp-6',
};

interface CollapsibleTextProps {
  children: React.ReactNode;
  /** Lines shown while collapsed. Default 2. */
  collapsedLines?: 1 | 2 | 3 | 4 | 5 | 6;
  className?: string;
  expandLabel?: string;
  collapseLabel?: string;
}

export function CollapsibleText({
  children,
  collapsedLines = 2,
  className = '',
  expandLabel = 'Show more',
  collapseLabel = 'Show less',
}: CollapsibleTextProps) {
  const [expanded, setExpanded] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const contentId = useId();

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el || expanded) return;
    const measure = () => {
      // +1 tolerates sub-pixel rounding between the two heights.
      setTruncated(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    // A web-font swap changes glyph widths after first paint — re-measure.
    let cancelled = false;
    void document.fonts?.ready?.then(() => {
      if (!cancelled) measure();
    });
    return () => {
      cancelled = true;
      ro.disconnect();
    };
  }, [expanded, children, collapsedLines]);

  return (
    <div className={className}>
      <div
        id={contentId}
        ref={contentRef}
        className={
          expanded
            ? 'max-h-72 overflow-y-auto overscroll-contain pr-1'
            : CLAMP[collapsedLines]
        }
      >
        {children}
      </div>
      {(truncated || expanded) && (
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={contentId}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          className="mt-1 inline-flex items-center gap-0.5 text-meta font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 transition-colors"
        >
          {expanded ? collapseLabel : expandLabel}
          {expanded ? (
            <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
          )}
        </button>
      )}
    </div>
  );
}
